/**
 * @fileoverview Zod schema for the Invite HR Admin form (Invite HR Admin, 3.5.11.31).
 * @description The invitee's first, optional middle and last name (1-100 characters each), email and
 * Indian mobile number. The country code is not a field: every number is sent with +91. Every issue
 * message is a translation key, translated at the display call site.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { z } from 'zod'

import { emailField, mobileNumberField } from '@/shared/lib/validation/contact-fields.schema'

const NAME_MAX_LENGTH = 100

/** @description The Invite HR Admin form's rules. */
export const inviteHrAdminFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'sales.activeClients.invite.errors.firstNameRequired')
    .max(NAME_MAX_LENGTH, 'sales.activeClients.invite.errors.nameTooLong'),
  middleName: z
    .string()
    .trim()
    .max(NAME_MAX_LENGTH, 'sales.activeClients.invite.errors.nameTooLong')
    .optional(),
  lastName: z
    .string()
    .trim()
    .min(1, 'sales.activeClients.invite.errors.lastNameRequired')
    .max(NAME_MAX_LENGTH, 'sales.activeClients.invite.errors.nameTooLong'),
  email: emailField({
    required: 'sales.activeClients.invite.errors.emailRequired',
    invalid: 'sales.activeClients.invite.errors.emailInvalid',
  }),
  mobileNumber: mobileNumberField(),
})

/** @description The Invite HR Admin form's values. */
export type InviteHrAdminFormValues = z.infer<typeof inviteHrAdminFormSchema>
